// Reglas puras de condición de pago de una venta: cuántos días, cómo se
// etiqueta, cuándo va a cuenta corriente y cuándo vence.
//
// Viven acá y no en salesReceivables.js porque ese módulo importa `../supabase`
// a nivel de archivo, que lee `import.meta.env` — algo que solo existe bajo
// Vite. Cualquier módulo que necesite estas reglas y tenga que poder cargarse
// fuera del browser (tests con `node --test`, por ejemplo) reventaba al
// importarlas, y la salida fácil era copiarlas. Con dos copias de la regla que
// decide si una venta genera deuda, la primera que cambie deja a la otra
// facturando distinto en silencio.
//
// salesReceivables.js las re-exporta, así que los importadores existentes
// siguen funcionando sin cambios.
import { addSettlementDays } from './paymentSettlement.js';
import {
  isConsumidorFinalTaxCondition,
  isGenericConsumidorFinalClient,
} from './consumidorFinal.js';

const toDateOnly = (value) => String(value || '').slice(0, 10);

export function normalizePaymentConditionDays(value, fallback = 0) {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) {
    return Math.max(0, Math.floor(parsed));
  }

  const fallbackValue = Number(fallback);
  if (Number.isFinite(fallbackValue) && fallbackValue >= 0) {
    return Math.max(0, Math.floor(fallbackValue));
  }

  return 0;
}

export function getClientPaymentConditionDays(client) {
  const directValue = Number(client?.payment_condition_days);
  if (Number.isFinite(directValue) && directValue >= 0) {
    return Math.max(0, Math.floor(directValue));
  }

  const rawCondition = String(client?.default_payment_condition || '').toLowerCase();
  if (!rawCondition || rawCondition.includes('contado')) return 0;

  const match = rawCondition.match(/(\d+)/);
  return match ? Math.max(0, Number(match[1])) : 0;
}

export function formatPaymentConditionLabel(days) {
  const normalizedDays = normalizePaymentConditionDays(days);
  return normalizedDays === 0 ? 'Contado' : `${normalizedDays} dias`;
}

export function hasClientCreditTerms(client) {
  const directDays = getClientPaymentConditionDays(client);
  if (directDays > 0) return true;

  const rawCondition = String(client?.default_payment_condition || '').trim().toLowerCase();
  if (!rawCondition) return false;

  return !rawCondition.includes('contado');
}

export function shouldDeferNonConsumerCollection(organization, client) {
  if (!client) return false;
  return Boolean(organization?.defer_non_cf_sales_collection) && !isConsumidorFinalTaxCondition(client);
}

export function getSalePaymentConditionDays({
  sale,
  client,
  paymentConditionDays,
} = {}) {
  const directValue = paymentConditionDays ?? sale?.payment_condition_days;
  const normalizedDirectValue = normalizePaymentConditionDays(directValue, null);

  if (directValue != null && Number.isFinite(Number(directValue))) {
    return normalizedDirectValue;
  }

  return getClientPaymentConditionDays(client);
}

export function getSalePaymentConditionLabel({
  sale,
  client,
  paymentConditionDays,
} = {}) {
  const directLabel = String(sale?.payment_condition_label || '').trim();
  if (directLabel) return directLabel;
  return formatPaymentConditionLabel(
    getSalePaymentConditionDays({ sale, client, paymentConditionDays }),
  );
}

export function shouldUseClientAccountForSale({
  organization,
  client,
  sale,
  paymentConditionDays,
} = {}) {
  // El consumidor final generico/anonimo (venta de mostrador) es contado por
  // definicion: nunca debe quedar pendiente de cobro en cuenta corriente,
  // aunque tenga configurada una condicion de venta en dias.
  if (isGenericConsumidorFinalClient(client)) return false;

  const normalizedDays = getSalePaymentConditionDays({
    sale,
    client,
    paymentConditionDays,
  });

  if (normalizedDays > 0) return true;
  return shouldDeferNonConsumerCollection(organization, client);
}

export function getSaleReceivableDueDate({
  createdAt,
  client,
  paymentConditionDays,
}) {
  const dueIso = addSettlementDays(
    createdAt || new Date().toISOString(),
    getSalePaymentConditionDays({ client, paymentConditionDays }),
    false,
  );
  return dueIso.slice(0, 10);
}

export function buildSalePaymentTerms({
  createdAt,
  sale,
  client,
  paymentConditionDays,
} = {}) {
  const days = getSalePaymentConditionDays({
    sale,
    client,
    paymentConditionDays,
  });
  const label = getSalePaymentConditionLabel({
    sale,
    client,
    paymentConditionDays: days,
  });
  const dueDate =
    toDateOnly(sale?.payment_due_date) ||
    getSaleReceivableDueDate({
      createdAt: createdAt || sale?.created_at,
      client,
      paymentConditionDays: days,
    });

  return { days, label, dueDate };
}
